import { Menu_salesCategories } from './__generated__/Menu';
import { SalesCategory, MenuCategory, NewMenuCategory } from './types';
import { MenuCategoryInput } from '../../../globalTypes';

export const isNewMenuCategory = (menuCategory: MenuCategory): menuCategory is NewMenuCategory => {
  return (menuCategory as NewMenuCategory).tempId !== undefined;
};

export type SalesCategoryWithChildren = SalesCategory & {
  children: SalesCategoryWithChildren[];
};

export const transformCategoriesForAPI = (categories: MenuCategory[]): MenuCategoryInput[] => {
  return categories.map((category) => {
    if (isNewMenuCategory(category)) {
      return {
        name: category.name,
        salesCategoryId: category.salesCategoryId,
      };
    }

    return {
      id: category.id,
      name: category.name,
      salesCategoryId: category.salesCategory.id,
    };
  });
};

export const canSubmitCategories = (categories: MenuCategory[]): boolean => {
  return categories.every(canSubmitCategory);
};

export const canSubmitCategory = (category: MenuCategory): boolean => {
  if (isNewMenuCategory(category)) {
    return !!(category.name.trim().length && category.salesCategoryId);
  }

  return !!category.name.trim().length;
};

const flattenSaleCategory = (saleCategories: SalesCategoryWithChildren[]): SalesCategory[] => {
  const final: SalesCategory[] = [];
  const stack = [saleCategories];

  let currentIndex = 0;
  while (stack.length > 0) {
    let currentList = stack[currentIndex];
    if (currentList.length == 0) {
      stack.pop();
      currentIndex--;
      continue;
    }

    const saleCategory = currentList.shift();
    const prefix = '-'.repeat(currentIndex);

    final.push({
      id: saleCategory.id,
      name: currentIndex === 0 ? saleCategory.name : prefix + ' ' + saleCategory.name,
    });

    stack.push(saleCategory.children);
    currentIndex++;
  }

  return final;
};

export const createSalesCategoryListWithIndents = (salesCategories: Menu_salesCategories[]): SalesCategory[] => {
  const saleCategoryWithChilden = createHierarchyFromSalesCategories(salesCategories);

  return flattenSaleCategory(saleCategoryWithChilden);
};

const createHierarchyFromSalesCategories = (salesCategories: Menu_salesCategories[]): SalesCategoryWithChildren[] => {
  const byId: { [id: string]: SalesCategoryWithChildren } = {};
  for (let saleCategory of salesCategories) {
    byId[saleCategory.id] = {
      id: saleCategory.id,
      name: saleCategory.name,
      children: [],
    };
  }

  const tree = [];
  for (let saleCategory of salesCategories) {
    if (saleCategory.parentId) {
      byId[saleCategory.parentId].children.push(byId[saleCategory.id]);
    } else {
      tree.push(byId[saleCategory.id]);
    }
  }

  return tree;
};
